import axiosInstance from "@/config/axiosInstance";

// ==================== PUBLIC POST ENDPOINTS ====================

/**
 * Get all posts with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {boolean} params.isPublished - Filter by publish status
 * @param {boolean} params.isFeatured - Filter by featured status
 * @param {string} params.search - Search in title, excerpt, content
 * @param {number} params.categoryId - Filter by category ID
 * @returns {Promise<{data: Array, pagination: Object}>}
 */
export const getAllPosts = async (params = {}) => {
  const response = await axiosInstance.get("/api/posts", { params });
  return response.data;
};

/**
 * Get latest published posts (for homepage)
 * @param {number} limit - Number of posts to fetch (default: 6)
 * @returns {Promise<{data: Array}>}
 */
export const getLatestPosts = async (limit = 6) => {
  const response = await axiosInstance.get("/api/posts/latest", {
    params: { limit },
  });
  return response.data;
};

/**
 * Get posts by category slug
 * @param {string} slug - Category slug
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<{data: Array, pagination: Object}>}
 */
export const getPostsByCategory = async (slug, params = {}) => {
  const response = await axiosInstance.get(`/api/posts/category/${slug}`, { params });
  return response.data;
};

/**
 * Get single post by ID
 * @param {number} id - Post ID
 * @returns {Promise<{data: Object}>}
 */
export const getPostById = async (id) => {
  const response = await axiosInstance.get(`/api/posts/${id}`);
  return response.data;
};

/**
 * Get single post by slug
 * @param {string} slug - Post slug
 * @returns {Promise<{data: Object}>}
 */
export const getPostBySlug = async (slug) => {
  const response = await axiosInstance.get(`/api/posts/slug/${slug}`);
  return response.data;
};

/**
 * Get previous and next posts for navigation
 * @param {string} slug - Current post slug
 * @returns {Promise<{data: {previous: Object|null, next: Object|null}}>}
 */
export const getAdjacentPosts = async (slug) => {
  const response = await axiosInstance.get(`/api/posts/slug/${slug}/adjacent`);
  return response.data;
};

// ==================== ADMIN POST ENDPOINTS ====================

/**
 * Convert a File to base64 data URL
 * @param {File} file - File to convert
 * @returns {Promise<string>} - Base64 data URL
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Create new post (Admin only - requires JWT)
 * @param {Object} postData - Post data
 * @param {string} postData.title - Post title
 * @param {string} postData.content - Post HTML content
 * @param {string} postData.excerpt - Post excerpt
 * @param {number} postData.category_id - Category ID
 * @param {File} postData.featured_image - Featured image file (optional)
 * @param {boolean} postData.is_published - Publish status (default: false)
 * @param {boolean} postData.is_featured - Featured status (default: false)
 * @param {Array<string>} postData.tags - Array of tag names (optional)
 * @param {Array<File>} postData.section_images - Section image files (optional)
 * @returns {Promise<{data: Object}>}
 */
export const createPost = async (postData) => {
  const payload = {
    title: postData.title,
    content: postData.content,
    excerpt: postData.excerpt || "",
    category_id: postData.category_id,
    is_published: postData.is_published,
    is_featured: postData.is_featured,
    tags: postData.tags || [],
  };

  // Convert featured image to base64
  if (postData.featured_image instanceof File) {
    payload.featured_image = await fileToBase64(postData.featured_image);
  }

  // Convert section images to base64
  if (postData.section_images && postData.section_images.length > 0) {
    payload.section_images = await Promise.all(
      postData.section_images.map((file) => fileToBase64(file))
    );
  }

  const response = await axiosInstance.post("/api/posts", payload);
  return response.data;
};

/**
 * Update existing post (Admin only - requires JWT)
 * @param {number} id - Post ID
 * @param {Object} postData - Updated post data
 * @param {File|string} postData.featured_image - Image file or existing path
 * @param {Array<File>} postData.section_images - New section image files
 * @returns {Promise<{data: Object}>}
 */
export const updatePost = async (id, postData) => {
  const payload = {};

  if (postData.title !== undefined) payload.title = postData.title;
  if (postData.content !== undefined) payload.content = postData.content;
  if (postData.excerpt !== undefined) payload.excerpt = postData.excerpt;
  if (postData.category_id !== undefined) payload.category_id = postData.category_id;
  if (postData.is_published !== undefined) payload.is_published = postData.is_published;
  if (postData.is_featured !== undefined) payload.is_featured = postData.is_featured;
  if (postData.tags) payload.tags = postData.tags;

  // Convert featured image to base64 if it's a File, otherwise pass existing path or null
  if (postData.featured_image instanceof File) {
    payload.featured_image = await fileToBase64(postData.featured_image);
  } else if (postData.featured_image === null) {
    // Explicitly set to null to signal backend to delete the old image
    payload.featured_image = null;
  } else if (typeof postData.featured_image === "string" && postData.featured_image) {
    payload.featured_image = postData.featured_image;
  }

  // Convert section images to base64
  if (postData.section_images && postData.section_images.length > 0) {
    payload.section_images = await Promise.all(
      postData.section_images.map((file) => fileToBase64(file))
    );
  }

  const response = await axiosInstance.put(`/api/posts/${id}`, payload);
  return response.data;
};

/**
 * Delete post (Admin only - requires JWT)
 * @param {number} id - Post ID
 * @returns {Promise<{message: string}>}
 */
export const deletePost = async (id) => {
  const response = await axiosInstance.delete(`/api/posts/${id}`);
  return response.data;
};

/**
 * Toggle post publish status (Admin only - requires JWT)
 * @param {number} id - Post ID
 * @param {boolean} isPublished - New publish status
 * @returns {Promise<{data: Object}>}
 */
export const togglePublishPost = async (id, isPublished) => {
  const response = await axiosInstance.patch(`/api/posts/${id}/publish`, {
    is_published: isPublished,
  });
  return response.data;
};
